import type { ReactNode } from "react";
import {
  CalendarOutlined,
  FileTextOutlined,
  SunOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import classNames from "classnames";

import LoadingCircle from "../../neo/LoadingCircle";
import { isMondayBriefingName } from "../../pages/Home/homeWorkflowActivity";
import styles from "./styles.module.scss";

/**
 * How a workflow looks wherever it's listed (Home, Templates, My Workflows):
 * its display name, icon tile, category tag and status pill.
 */

/** The weekly briefing is still called "Monday Morning Briefing" in the
 *  catalog; users know it as the Weekly Clinical Briefing. */
export const WEEKLY_BRIEFING_NAME = "Weekly Clinical Briefing";

export const workflowDisplayName = (name: string): string =>
  isMondayBriefingName(name) ? WEEKLY_BRIEFING_NAME : name;

type IconTone = "amber" | "orange" | "green" | "blue" | "violet" | "teal";

const HASHED_TONES: IconTone[] = ["blue", "green", "violet", "teal"];

const workflowLook = (
  name: string,
  custom: boolean
): { icon: ReactNode; tone: IconTone } => {
  const key = name.trim().toLowerCase();
  if (isMondayBriefingName(name)) {
    return { icon: <CalendarOutlined />, tone: "amber" };
  }
  if (key.includes("daily clinical")) {
    return { icon: <SunOutlined />, tone: "orange" };
  }
  if (key.includes("concern") || key.includes("grievance")) {
    return { icon: <FileTextOutlined />, tone: "green" };
  }
  // Anything else keeps a colour picked from its name.
  let hash = 0;
  for (const char of key) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return {
    icon: custom ? <ThunderboltOutlined /> : <FileTextOutlined />,
    tone: HASHED_TONES[Math.abs(hash) % HASHED_TONES.length],
  };
};

export const WorkflowIcon = ({
  name,
  custom = false,
  size = "md",
}: {
  /** The catalog (or Neo) name; the look is keyed on it. */
  name: string;
  /** A workflow the user built in Neo rather than a catalog one. */
  custom?: boolean;
  /** ``list`` (48px) matches a name + one-line description beside it. */
  size?: "sm" | "md" | "list" | "lg";
}) => {
  const { icon, tone } = workflowLook(name, custom);
  return (
    <span
      className={classNames(
        styles.icon,
        styles[`icon_${size}`],
        styles[`tone_${tone}`]
      )}
      aria-hidden
    >
      {icon}
    </span>
  );
};

export const categoryLabel = (category?: string | null): string | null =>
  category
    ? category
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : null;

const CATEGORY_TONES = new Set([
  "clinical",
  "audits",
  "revenue",
  "operations",
  "marketing",
]);

export const WorkflowCategoryTag = ({
  category,
  fallback,
}: {
  category?: string | null;
  /** Shown (in the neutral tone) when there's no category. */
  fallback?: string;
}) => {
  const label = categoryLabel(category) ?? fallback;
  if (!label) return null;
  const key = label.toLowerCase();
  return (
    <span
      className={classNames(
        styles.category,
        styles[`category_${CATEGORY_TONES.has(key) ? key : "general"}`]
      )}
    >
      {label}
    </span>
  );
};

export type WorkflowStatusTone =
  | "running"
  | "success"
  | "warning"
  | "error"
  | "idle";

export const statusTone = (status?: string | null): WorkflowStatusTone => {
  const value = (status ?? "").toLowerCase();
  // A built report waiting for its send window: the output is ready.
  if (value === "generated" || value === "ready_to_send") return "success";
  // Negated forms ("unsent", "not_delivered", "incomplete") must not fall
  // through to the substring success checks below.
  if (
    /\b(?:un|in|not[\s_-]*)(?:sent|deliver|complete)/.test(value) ||
    value.includes("fail") ||
    value.includes("error") ||
    value.includes("blocked") ||
    value.includes("cancel") ||
    value.includes("bounce") ||
    value.includes("reject")
  ) {
    return "error";
  }
  if (
    value.includes("partial") ||
    value.includes("awaiting") ||
    value.includes("pending") ||
    value.includes("queued") ||
    // Amber like Home's "Scheduled" (a catalog workflow between runs).
    value === "scheduled"
  ) {
    return "warning";
  }
  if (
    value === "done" ||
    value.includes("sent") ||
    value.includes("delivered") ||
    value.includes("complete")
  ) {
    return "success";
  }
  if (
    value.includes("running") ||
    value.includes("generat") ||
    value.includes("deliver")
  ) {
    return "running";
  }
  return "idle";
};

export const statusLabel = (status?: string | null): string => {
  if (!status) return "No runs yet";
  // ``generated`` (a run) and ``ready_to_send`` (a workflow) both mean the
  // report is built and waits for its send window, so it isn't "Done" yet.
  const value = status.toLowerCase();
  if (value === "generated" || value === "ready_to_send") {
    return "Ready to send";
  }
  return status
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

/** A status pill. Pass ``status`` (a raw run/workflow status) or an explicit
 *  ``tone`` + ``label``. */
export const WorkflowStatusTag = ({
  status,
  tone,
  label,
  icon,
}: {
  status?: string | null;
  tone?: WorkflowStatusTone;
  label?: string;
  icon?: ReactNode;
}) => {
  const resolvedTone = tone ?? statusTone(status);
  return (
    <span
      className={classNames(styles.status, styles[`status_${resolvedTone}`])}
    >
      {icon ?? (resolvedTone === "running" ? <LoadingCircle /> : null)}
      {label ?? statusLabel(status)}
    </span>
  );
};
